import React, { useState } from 'react';
import {
  Download,
  Share2,
  AlertTriangle,
  Target,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  BookOpen,
  Star,
  FileText,
  Video
} from 'lucide-react';
import { PipelineSummary } from './AnalysisProgress';

const TacticalReport = ({ analysisData, onShare, onDownload, onFeedback }) => {
  const [activeTab, setActiveTab] = useState('tactical');

  if (!analysisData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No analysis data available</p>
      </div>
    );
  }

  // Handle both formats: backend full analysis OR chat saved analysis
  const isChatAnalysis = analysisData.analysis_data && !analysisData.results;
  
  // ✅ DEBUG: Log what we're receiving
  console.log('🔍 TacticalReport received data:', {
    isChatAnalysis,
    hasMetadata: !!analysisData.metadata,
    hasStatistics: !!analysisData.statistics,
    metadataDuration: analysisData.metadata?.duration_seconds,
    statisticsFrames: analysisData.statistics?.frames_analyzed,
    directDuration: analysisData.duration_seconds,
    directFrames: analysisData.frames_analyzed,
    allKeys: Object.keys(analysisData)
  });
  
  let displayData = {};
  
  if (isChatAnalysis) {
  // Chat analysis format OR Video analysis format
  displayData = {
    opponentTeam: analysisData.opponent_team,
    matchDate: analysisData.match_date,
    createdAt: analysisData.created_at,
    processingTime: analysisData.processing_time_seconds,
    framesAnalyzed: analysisData.analysis_data?.total_frames_analyzed || 0,
    results: {
      tactical: {
        fullReport: analysisData.analysis_data?.fullReport || analysisData.analysis_data?.full_analysis || '',
        weaknesses: analysisData.weaknesses || [],
        strategies: analysisData.strategies || [],
        formationRecommendation: analysisData.formation_recommendation || analysisData.analysis_data?.formation_detection || '',
        keyPlayers: analysisData.key_players || []
      }
    }
  };
  } else {
 // Backend full analysis format
  // ✅ FIX: Read from analysis_data.metadata and analysis_data.statistics
  const processingTime = 
    analysisData.analysis_data?.metadata?.duration_seconds ||  // ✅ NEW PATH
    analysisData.metadata?.duration_seconds ||
    analysisData.processing_time_seconds ||
    analysisData.processing_time ||
    0;
  
  const framesAnalyzed = 
    analysisData.analysis_data?.statistics?.frames_analyzed ||  // ✅ NEW PATH
    analysisData.statistics?.frames_analyzed ||
    analysisData.frames_analyzed ||
    0;
  
  const totalPlayers = 
    analysisData.analysis_data?.statistics?.total_players ||  // ✅ NEW PATH
    analysisData.statistics?.total_players ||
    analysisData.total_players ||
    0;
  
  displayData = {
    ...analysisData,
    processingTime,
    framesAnalyzed,
    totalPlayers
  };
}

  const { results, opponentTeam, matchDate, processingTime, createdAt } = displayData;

  // Only show tabs that have data
  const tabs = [
    { id: 'tactical', label: 'Tactical Analysis', icon: Target, show: true },
    { id: 'fulltext', label: 'Full Report', icon: FileText, show: !!(displayData.results?.tactical?.fullReport || analysisData.analysis_data?.full_analysis) },
    { id: 'formation', label: 'Formation', icon: Users, show: results?.formation },
    { id: 'movement', label: 'Player Movement', icon: TrendingUp, show: results?.playerMovement },
    { id: 'training', label: 'Training Plan', icon: BookOpen, show: results?.training }
  ].filter(tab => tab.show);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-luxury p-6 mb-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              ⚽ Tactical Analysis Report
            </h1>
            <p className="text-xl text-blue-100">
              Opponent: <span className="font-bold text-gold-300">{opponentTeam || 'Unknown Team'}</span>
            </p>
            {analysisData.confidence_score && (
              <div className="mt-2 flex items-center space-x-2">
                <Star className="h-5 w-5 text-gold-400" />
                <span className="text-blue-100">Confidence: {analysisData.confidence_score}%</span>
              </div>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onShare}
              className="flex items-center space-x-2 px-4 py-2 bg-white hover:bg-gray-100 text-blue-600 rounded-lg transition-all font-semibold shadow-lg"
            >
              <Share2 className="h-5 w-5" />
              <span>Share</span>
            </button>
            
            <button
              onClick={onDownload}
              className="flex items-center space-x-2 px-4 py-2 bg-gold-500 hover:bg-gold-600 text-royal-900 rounded-lg transition-all font-semibold shadow-lg"
            >
              <Download className="h-5 w-5" />
              <span>Export PDF</span>
            </button>
          </div>
        </div>

        {/* Meta Information - ✅ FIXED: Now displays real data from backend */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-blue-100 border-t border-blue-500 pt-4 mt-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4" />
            <span>Generated: {new Date(createdAt || Date.now()).toLocaleDateString()}</span>
          </div>
          {matchDate && (
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4" />
              <span>Match Date: {new Date(matchDate).toLocaleDateString()}</span>
            </div>
          )}
          {processingTime > 0 && (
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4" />
              <span>Processing Time: {processingTime}s</span>
            </div>
          )}
          {/* ✅ NEW: Display frames analyzed from Python API */}
          {displayData.framesAnalyzed > 0 && (
            <div className="flex items-center space-x-2">
              <Video className="h-4 w-4" />
              <span>Frames Analyzed: {displayData.framesAnalyzed}</span>
            </div>
          )}
          {/* ✅ NEW: Display total players detected */}
          {displayData.totalPlayers > 0 && (
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Players Detected: {displayData.totalPlayers}</span>
            </div>
          )}
          {analysisData.data_source && (
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4" />
              <span>Source: {analysisData.data_source}</span>
            </div>
          )}
        </div>
      </div>

      {/* Pipeline Summary — show what the AI did behind the scenes */}
      {(analysisData.pipeline || analysisData.analysis_data?.pipeline) && (
        <div className="mb-6">
          <PipelineSummary pipeline={analysisData.pipeline || analysisData.analysis_data?.pipeline} />
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow-lg mb-6">
        <div className="border-b-2 border-gray-200">
          <nav className="flex space-x-8 px-6 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center space-x-2 py-4 px-1 border-b-2 font-semibold text-sm transition-all whitespace-nowrap
                    ${activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'tactical' && results?.tactical && (
            <TacticalAnalysisTab data={results.tactical} />
          )}

          {activeTab === 'fulltext' && results?.tactical?.fullReport && (
            <FullReportTab content={results.tactical.fullReport} />
          )}
          
          {activeTab === 'formation' && results?.formation && (
            <FormationTab data={results.formation} />
          )}
          
          {activeTab === 'movement' && results?.playerMovement && (
            <PlayerMovementTab data={results.playerMovement} />
          )}
          
          {activeTab === 'training' && results?.training && (
            <TrainingTab data={results.training} />
          )}
        </div>
      </div>

      {/* Feedback Section */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Rate This Analysis
        </h3>
        <div className="flex items-center space-x-4">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              onClick={() => onFeedback && onFeedback(rating)}
              className="text-gray-300 hover:text-yellow-400 transition-colors transform hover:scale-110"
            >
              <Star className="h-8 w-8 fill-current" />
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-3">
          Your feedback helps us improve TAHLEEL.ai for coaches worldwide!
        </p>
      </div>
    </div>
  );
};

// Full Report Tab (for chat analyses)
const FullReportTab = ({ content }) => (
  <div className="prose max-w-none">
    <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-xl p-6">
      <div className="whitespace-pre-wrap text-gray-800 leading-relaxed font-medium">
        {content}
      </div>
    </div>
  </div>
);

// Tactical Analysis Tab Component
const TacticalAnalysisTab = ({ data }) => {
  // Handle string arrays (chat format) vs object arrays (backend format)
  const weaknesses = Array.isArray(data.weaknesses) 
    ? data.weaknesses.map(w => typeof w === 'string' ? { weakness: w } : w)
    : [];
  
  const strategies = Array.isArray(data.strategies)
    ? data.strategies.map(s => typeof s === 'string' ? { strategy: s } : s)
    : [];

  const keyPlayers = Array.isArray(data.keyPlayers)
    ? data.keyPlayers.map(p => typeof p === 'string' ? { name: p } : p)
    : [];

  return (
    <div className="space-y-6">
      {/* Weaknesses */}
      {weaknesses.length > 0 && (
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <AlertTriangle className="h-7 w-7 text-red-600 mr-3" />
            Opponent Weaknesses
          </h3>
          <div className="grid gap-4">
            {weaknesses.map((weakness, index) => (
              <div key={index} className="bg-red-50 border-l-4 border-red-600 rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                        WEAKNESS #{index + 1}
                      </span>
                    </div>
                    <p className="text-gray-900 font-medium text-lg mb-2">
                      {weakness.area || weakness.weakness || weakness.title || weakness}
                    </p>
                    {weakness.description && weakness.description !== weakness.weakness && (
                      <p className="text-gray-700 mb-2">
                        {weakness.description}
                      </p>
                    )}
                    {weakness.exploitation && (
                      <div className="mt-3 p-3 bg-white rounded border border-red-200">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-red-700">💡 How to Exploit:</span> {weakness.exploitation}
                        </p>
                      </div>
                    )}
                  </div>
                  {weakness.confidence && (
                    <div className="ml-4 text-right">
                      <div className="text-3xl font-bold text-red-600">
                        {weakness.confidence}%
                      </div>
                      <div className="text-xs text-gray-500">Confidence</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Counter Strategies */}
      {strategies.length > 0 && (
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <Target className="h-7 w-7 text-green-600 mr-3" />
            Recommended Counter Strategies
          </h3>
          <div className="grid gap-4">
            {strategies.map((strategy, index) => (
              <div key={index} className="bg-green-50 border-l-4 border-green-600 rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">
                    STRATEGY #{index + 1}
                  </span>
                </div>
                <h4 className="font-semibold text-gray-900 text-lg mb-2">
                  {strategy.title || `Counter Strategy ${index + 1}`}
                </h4>
                <p className="text-gray-700">
                  {strategy.description || strategy.strategy || strategy}
                </p>
                {strategy.implementation && (
                  <div className="mt-3 p-3 bg-white rounded border border-green-200">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">⚡ Implementation:</span> {strategy.implementation}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Players to Target */}
      {keyPlayers.length > 0 && (
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <Users className="h-7 w-7 text-blue-600 mr-3" />
            Key Players to Target
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {keyPlayers.map((player, index) => (
              <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <h4 className="font-bold text-gray-900 text-lg">{player.name || player}</h4>
                {player.role && (
                  <p className="text-sm text-blue-600 font-medium mb-2">{player.role}</p>
                )}
                {player.weakness && (
                  <p className="text-sm text-gray-700">{player.weakness}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Formation Recommendation */}
      {data.formationRecommendation && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-100 mb-2">✨ Recommended Formation</h3>
              <p className="text-4xl font-black">{data.formationRecommendation}</p>
            </div>
            <CheckCircle className="h-16 w-16 text-gold-400" />
          </div>
        </div>
      )}

      {/* If no structured data, show message */}
      {weaknesses.length === 0 && strategies.length === 0 && !data.formationRecommendation && (
        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-600 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">
            No structured tactical data available. Please check the "Full Report" tab for the complete analysis.
          </p>
        </div>
      )}
    </div>
  );
};

// Formation Tab Component
const FormationTab = ({ data }) => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-blue-50 border-2 border-blue-600 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Detected Formation</h3>
        <div className="text-center mb-4">
          <div className="text-5xl font-bold text-blue-600 mb-2">{data.primary}</div>
          <div className="text-sm text-gray-600">
            Confidence: <span className="font-semibold">{data.confidence}%</span>
          </div>
        </div>
      </div>

      {data.counterFormation && (
        <div className="bg-green-50 border-2 border-green-600 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Recommended Counter</h3>
          <div className="text-center mb-4">
            <div className="text-5xl font-bold text-green-600 mb-2">{data.counterFormation}</div>
            <div className="text-sm text-gray-600">Best matchup against opponent</div>
          </div>
        </div>
      )}
    </div>

    {data.strengths && data.strengths.length > 0 && (
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-3">Formation Strengths</h3>
        <ul className="space-y-2">
          {data.strengths.map((strength, index) => (
            <li key={index} className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">{strength}</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {data.weaknesses && data.weaknesses.length > 0 && (
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-3">Formation Weaknesses</h3>
        <ul className="space-y-2">
          {data.weaknesses.map((weakness, index) => (
            <li key={index} className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">{weakness}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// Player Movement Tab
const PlayerMovementTab = ({ data }) => (
  <div className="space-y-6">
    {data.keyPlayers && data.keyPlayers.length > 0 && (
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Key Player Analysis</h3>
        <div className="grid gap-4">
          {data.keyPlayers.map((player, index) => (
            <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">{player.name}</h4>
              <div className="space-y-1 text-sm text-gray-700">
                {player.position && <p><strong>Position:</strong> {player.position}</p>}
                {player.heatmap && <p><strong>Activity:</strong> {player.heatmap}</p>}
                {player.influence && <p><strong>Influence:</strong> {player.influence}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {data.movementPatterns && data.movementPatterns.length > 0 && (
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Movement Patterns</h3>
        <div className="space-y-3">
          {data.movementPatterns.map((pattern, index) => (
            <div key={index} className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
              <p className="text-gray-700">{pattern}</p>
            </div>
          ))}
        </div>
      </div>
    )}

    {data.vulnerabilities && data.vulnerabilities.length > 0 && (
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">Tactical Vulnerabilities</h3>
        <div className="grid gap-3">
          {data.vulnerabilities.map((vuln, index) => (
            <div key={index} className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
              <p className="text-gray-700">{vuln}</p>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Training Tab
const TrainingTab = ({ data }) => (
  <div className="space-y-6">
    {data.totalHours && (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="text-center">
          <div className="text-4xl font-bold text-blue-600 mb-2">{data.totalHours} Hours</div>
          <div className="text-sm text-gray-600">Total Weekly Training Volume</div>
        </div>
      </div>
    )}

    {data.dailySessions && data.dailySessions.length > 0 && (
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">7-Day Training Plan</h3>
        <div className="space-y-4">
          {data.dailySessions.map((session, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-gray-900">{session.day}</h4>
                  <p className="text-sm text-gray-600">{session.focus}</p>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-blue-600">{session.duration}</div>
                  <div className="text-xs text-gray-500">{session.intensity}</div>
                </div>
              </div>
              
              {session.drills && session.drills.length > 0 && (
                <div className="space-y-2">
                  {session.drills.map((drill, drillIndex) => (
                    <div key={drillIndex} className="bg-gray-50 rounded p-3">
                      <div className="font-medium text-gray-900 mb-1">{drill.name}</div>
                      <p className="text-sm text-gray-600">{drill.description}</p>
                      {drill.duration && (
                        <div className="text-xs text-gray-500 mt-1">Duration: {drill.duration}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default TacticalReport;
